import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Col,

  message,
  Modal,
  Progress,
  Row,
  Spin,
  Steps,
  theme,
} from "antd";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuote } from "./QuoteProvider";
// Funnel steps are eager-imported (NOT lazy). They're the core conversion
// flow, and lazy chunk-load + Suspense remounts made the timing of each
// step's save non-deterministic — which fed a lost-update race in
// updateQuote (a stale full-snapshot write landing last and clobbering
// patient-entered fields). Eager keeps the steps in the main bundle so each
// step's save fires against fully-mounted, stable state. Perf is already
// near its mobile ceiling and the steps are small next to the AntD/landing
// payload, so the LCP/unused-JS cost is marginal. (StepSelectProcedureV2 was
// already eager as the first step so LCP doesn't wait on a second chunk.)
import StepSelectProcedure from "./steps/StepSelectProcedure";
import StepSelectSurgeon from "./steps/StepSelectSurgeon";
import StepPhotos from "./steps/StepPhotos";
import StepContact from "./steps/StepContact";
import StepSelectLocation from "./steps/StepSelectLocation";
import StepMedicalHistory from "./steps/StepMedicalHistory";
import StepReadinessTimeline from "./steps/StepReadinessTimeline";
import {
  EnvironmentOutlined,
  FileAddOutlined,
  FileImageOutlined,
  FileTextOutlined,
  HeartOutlined,
  LeftCircleOutlined,
} from "@ant-design/icons";
import { createGlobalStyle } from "styled-components";
const QuoteSuccessModal = lazy(
  () => import("./components/QuoteSuccessModal/QuoteSuccessModal"),
);
import { Surgeon } from "../../types/surgeons";
// @ts-ignore
import { Trans, useTranslation } from "react-i18next";
import InAppBrowserBanner from "./components/InAppBrowserBanner/InAppBrowserBanner";
import { useGAEventLogger } from "../hooks/useGAEventLogger";
import { useAppConfig } from "../hooks/useAppConfig";
import logo from "../../img/logo-tile.svg";
import { useIsIframe } from "../../hooks/useIsIframe";
import { useIsMobile } from "./MobileBreakpointContext";
import LanguageSwitcher from "../LanguageSwitcher";
const BookingSuccessPage = lazy(
  () => import("../../pages/BookingSuccessPage"),
);
const BookingCancelledPage = lazy(
  () => import("../../pages/BookingCancelledPage"),
);
import {
  trackQuoteDraftStarted,
  trackQuoteContactSubmit,
  trackQuoteStepComplete,
  trackQuoteStepView,
  trackQuoteViewportSnapshot,
} from "../../utils/quoteAnalytics";
import { usePatienAuthProvider } from "../patient-portal/authentication/usePatientProvider";
import {
  missingContactFields,
  CONTACT_FIELD_LABEL_KEYS,
} from "./hooks/useQuoteFormValidation";



// Define an interface for the props
interface GlobalStyleProps {
  background?: string;
}

enum Step {
  SelectLocation = "SelectLocation",
  Contact = "Contact",
  SelectProcedure = "SelectProcedure",
  SelectSurgeon = "SelectSurgeon",
  MedicalHistory = "MedicalHistory",
  ReadinessTimeline = "ReadinessTimeline",
  Photos = "Photos",
}

// Map URL paths to Step enum
const pathToStep: Record<string, Step> = {
  "/": Step.SelectProcedure, // Default to first step
  "/select-location": Step.SelectLocation,
  "/select-procedure": Step.SelectProcedure,
  "/contact": Step.Contact,
  "/select-surgeon": Step.SelectSurgeon,
  "/medical-history": Step.MedicalHistory,
  "/readiness-timeline": Step.ReadinessTimeline,
  "/photos": Step.Photos,
};

// Map Step enum to URL paths
const stepToPath: Record<Step, string> = {
  [Step.SelectLocation]: "/select-location",
  [Step.SelectProcedure]: "/select-procedure",
  [Step.Contact]: "/contact",
  [Step.SelectSurgeon]: "/select-surgeon",
  [Step.MedicalHistory]: "/medical-history",
  [Step.ReadinessTimeline]: "/readiness-timeline",
  [Step.Photos]: "/photos",
};

// Step enum → analytics step name (matches existing trackQuoteStepComplete payloads)
const stepToAnalyticsName: Record<Step, string> = {
  [Step.SelectLocation]: "select_location",
  [Step.SelectProcedure]: "procedures",
  [Step.Contact]: "contact",
  [Step.SelectSurgeon]: "select_surgeon",
  [Step.MedicalHistory]: "medical_history",
  [Step.ReadinessTimeline]: "readiness_timeline",
  [Step.Photos]: "photos",
};

// ---------------------------------------------------------------------------
// FriendsPassBanner — shown at the top of the funnel when a gift pass is active or dead.
// ---------------------------------------------------------------------------
const FriendsPassBanner: React.FC = () => {
  const { t } = useTranslation("quote");
  const { giftPass, giftPassDead } = useQuote();

  if (giftPass) {
    // Match the email's formatting zone (clinic-local ET) so the banner and
    // the email never show different expiry dates to a non-ET viewer.
    const expiryDate = new Date(giftPass.expiresAt).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "America/New_York",
    });
    return (
      <div
        style={{
          background: "#fef9c3",
          borderBottom: "1px solid #fde047",
          padding: "10px 16px",
          textAlign: "center",
          fontSize: "14px",
          color: "#713f12",
        }}
      >
        {/* COPY: APPROVED 2026-06-11 — do not edit without legal re-review (FL §817.505).
            Approved presentation bolds only the "pass applied" clause and the
            $99 — the <0>/<1> tags in the i18n string map to the <strong>s. */}
        🎁{" "}
        <Trans
          t={t}
          i18nKey="giftPass.bannerActive"
          values={{ name: giftPass.giverFirstName, expiry: expiryDate }}
          components={[<strong key="b0" />, <strong key="b1" />]}
        />
      </div>
    );
  }
  if (giftPassDead === "redeemed") {
    return (
      <div
        style={{
          background: "#f3f4f6",
          borderBottom: "1px solid #e5e7eb",
          padding: "10px 16px",
          textAlign: "center",
          fontSize: "14px",
          color: "#6b7280",
        }}
      >
        {/* COPY: APPROVED 2026-06-11 — do not edit without legal re-review (FL §817.505) */}
        {t("giftPass.bannerRedeemed")}
      </div>
    );
  }
  if (giftPassDead === "expired") {
    return (
      <div
        style={{
          background: "#f3f4f6",
          borderBottom: "1px solid #e5e7eb",
          padding: "10px 16px",
          textAlign: "center",
          fontSize: "14px",
          color: "#6b7280",
        }}
      >
        {/* COPY: APPROVED 2026-06-11 — do not edit without legal re-review (FL §817.505) */}
        {t("giftPass.bannerExpired")}
      </div>
    );
  }
  return null;
};

const QuoteApp: React.FC = () => {
  // @ts-ignore
  const { t } = useTranslation("quote");
  const { logEvent } = useGAEventLogger();
  const { data: appConfig } = useAppConfig();
  const isIframe = useIsIframe();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const bookingParam = searchParams.get("booking");

  // Extract quoteId from params (could be from wildcard like {*: 'id/699...'} or direct param)
  let quoteId = params.quoteId || params.quote_id;
  if (!quoteId && params["*"]) {
    const wildcardPath = params["*"];
    const match = wildcardPath.match(/^(?:id|resume|photos)\/([a-f0-9]+)$/);
    if (match) {
      quoteId = match[1];
    }
  }

  // Detect resume mode: quoteId in URL means user is resuming an existing quote
  const isResumeMode = !!quoteId;

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Error boundary for debugging
  const [renderError, setRenderError] = useState<Error | null>(null);

  // Guard against navigation race conditions (especially in DevTools mobile simulation)
  const navigationInProgressRef = useRef(false);

  // contact-sweeps-v1 RETIRED 2026-06-20 (no_sweeps served 100%). Constant kept
  // so the contact-step analytics events below still carry the arm.
  const contactSweepsVariant = "no_sweeps";
  const quoteData = useQuote();
  const { token } = theme.useToken();
  const isMobile = useIsMobile();

  const { isSignedIn } = usePatienAuthProvider();

  const isAlreadyBooked = quoteData.quoteStatus === "BOOKED";

  // Get current path and determine current step. Memoized so identity is
  // stable across renders — the auto-redirect effect below depends on it
  // and would otherwise burn `replaceState` quota.
  const currentPath = useMemo(
    () =>
      location.pathname.replace(/^\/app\/quote/, "").replace(/\/$/, "") || "/",
    [location.pathname],
  );
  const resumeSubPath = isResumeMode
    ? location.pathname.replace(/^\/id\/[a-f0-9]+/, "").replace(/\/$/, "")
    : "";
  const currentStepFromPath = isResumeMode
    ? pathToStep[resumeSubPath] || Step.Photos
    : pathToStep[currentPath] || Step.SelectProcedure;

  // True when the user arrived via a step-targeted continue URL like
  // /id/<id>/medical-history (used by the SMS continue link for incomplete
  // META_LEAD / WEBSITE_FORM quotes). Distinct from the legacy default-resume
  // mode (Photos), where back-nav is blocked. In continue mode we want the
  // user to advance through the funnel from their entry step, so we extend
  // stepOrder forward and let next()/prev() work as normal.
  const isContinueMode =
    isResumeMode &&
    !!resumeSubPath &&
    !!pathToStep[resumeSubPath] &&
    pathToStep[resumeSubPath] !== Step.Photos;

  // Step paths in `stepToPath` are bare (e.g. "/medical-history"). When the
  // user is in a resume URL pattern (`/id/<id>/...`), calling navigate() with
  // a bare path drops the `/id/<id>` prefix and bounces the user out of
  // resume mode entirely — losing the loaded quote and triggering the
  // first-step auto-redirect. This helper preserves the prefix so resume
  // navigation stays inside the resume URL pattern. Safe for legacy resume
  // modes too (next/prev are blocked there, so this is never called).
  const navigateToStep = (
    path: string,
    options?: { replace?: boolean },
  ) => {
    const target = isResumeMode && quoteId ? `/id/${quoteId}${path}` : path;
    navigate(target, options);
  };

  const topAestheticsUrl = `https://topaesthetics.com${
    typeof window !== "undefined" ? window.location.search : ""
  }`;

  // Calculate available surgeons
  const availableSurgeons = quoteData.getAvailableSurgeons();

  // Auto-select if only one surgeon is available
  useEffect(() => {
    if (
      availableSurgeons.length === 1 &&
      quoteData.selectedSurgeon?.id !== availableSurgeons[0].id
    ) {
      quoteData.updateSurgeon(availableSurgeons[0].id);
    }
  }, [availableSurgeons, quoteData.selectedSurgeon]);

  /**
   * Dynamic step order based on A/B variants.
   *
   * Variant A (Control):
   * 1. [Location]
   * 2. Procedures
   * 3. Contact
   * 4. [Surgeon]
   * 5. Photos
   *
   * Variant B (Preview):
   * 1. [Location]
   * 2. Procedures
   * 3. Preview Range
   * 4. Contact + Medical Snippet
   * 5. [Surgeon]
   * 6. Photos
   *
   * Medical History Step A/B Test:
   * - Control: Medical history stays in Contact step
   * - Treatment: Medical history moves to dedicated step between Photos and Contact
   *
   * Resume Mode:
   * When resuming an existing quote (quoteId in URL), only Photos step is shown
   *
   * Reloaded via ?quote_id= (thank-you handoff, not resume mode):
   * 1. [Location]
   * 2. Procedures
   * 3. Medical History
   * 4. Readiness
   * 5. [Surgeon]
   * 6. Photos
   * (Contact is skipped — the hero/contact form already captured it.)
   */
  // Memoized to stabilize the array identity across renders. Without this,
  // every render produces a new `stepOrder` reference, which causes the
  // auto-redirect effect below to re-fire on every render and (in some
  // edge cases) blow past the browser's history.replaceState rate limit.
  const stepOrder = useMemo(
    () => {
      // Continue-link flow: entry step + remaining funnel forward.
      // Contact/Photos/etc. are intentionally included so the user can
      // advance through whatever's left (Contact already has data from the
      // upstream lead source so it'll just confirm-and-next).
      if (isContinueMode) {
        const forward = [
          ...(quoteData.locationOptions.length > 1
            ? [Step.SelectLocation]
            : []),
          Step.SelectProcedure,
          Step.MedicalHistory,
          Step.ReadinessTimeline,
          ...(availableSurgeons.length > 1 &&
          quoteData.selectedProcedureTypes.length === 0
            ? [Step.SelectSurgeon]
            : []),
          Step.Photos,
        ];
        const entryIdx = forward.findIndex((s) => s === currentStepFromPath);
        return entryIdx >= 0 ? forward.slice(entryIdx) : [Step.Photos];
      }
      // Legacy single-step resume (photos default).
      if (isResumeMode) {
        return [Step.Photos];
      }
      // Standard funnel (non-resume). The Photos step is the booking step —
      // its QuoteSummary side panel hosts the countdown + Book Now CTA, so
      // there's no separate /book-now step to insert here.
      return [
        ...(quoteData.locationOptions.length > 1
          ? [Step.SelectLocation]
          : []),
        Step.SelectProcedure,
        // Contact is skipped for a reloaded quote (the lead source already
        // captured it) and for a signed-in patient. Medical History and
        // Readiness are skipped ONLY for a signed-in patient: a quote reloaded
        // through ?quote_id= (the thank-you handoff) arrives with none of that
        // data, so it walks the same Procedures → Medical → Readiness → Photos
        // order the SMS continue link already uses (owner decision 2026-10-17;
        // before this the reloaded lane jumped straight from Procedures to
        // Photos).
        ...(quoteData.quoteReloaded || isSignedIn ? [] : [Step.Contact]),
        ...(!isSignedIn ? [Step.MedicalHistory] : []),
        ...(!isSignedIn ? [Step.ReadinessTimeline] : []),
        ...(availableSurgeons.length > 1 &&
        quoteData.selectedProcedureTypes.length === 0
          ? [Step.SelectSurgeon]
          : []),
        Step.Photos,
      ];
    },
    [
      isContinueMode,
      isResumeMode,
      currentStepFromPath,
      quoteData.locationOptions.length,
      quoteData.quoteReloaded,
      isSignedIn,
      availableSurgeons.length,
      quoteData.selectedProcedureTypes.length,
    ],
  );

  // Calculate current step index from URL
  const current = stepOrder.findIndex((step) => step === currentStepFromPath);

  // Current step determined from URL

  // Redirect to first step if at root or on an unknown path. No-op when
  // we're already at the target — calling navigate(replace) on the same
  // URL still issues a `history.replaceState` and stacks toward the
  // browser's 100/10s rate limit if we're re-rendering rapidly.
  useEffect(() => {
    // Skip auto-navigation in resume mode - user should stay on the URL they visited
    if (isResumeMode) {
      return;
    }
    if (stepOrder.length === 0) return;

    const firstStepPath = stepToPath[stepOrder[0]];
    const needsRedirect =
      currentPath === "/" ||
      (!pathToStep[currentPath] && currentPath !== "/");

    if (needsRedirect && currentPath !== firstStepPath) {
      navigate(firstStepPath, { replace: true });
    }
  }, [currentPath, stepOrder, navigate, isResumeMode]);

  // Reset navigation guard when location changes (completes navigation)
  useEffect(() => {
    navigationInProgressRef.current = false;
  }, [location.pathname]);

  // Close the success modal if the user navigates away from the step that opened it
  useEffect(() => {
    setIsModalOpen(false);
  }, [location.pathname]);

  // Auto-open the success modal when arriving from Stripe with
  // `?booking=success`. The Stripe webhook flips simpleStatus to BOOKED
  // before the redirect, so the user lands here already paid. The modal
  // derives its "booked" copy from bookingParam at the render site below.
  useEffect(() => {
    if (bookingParam === "success" && isResumeMode) {
      setIsModalOpen(true);
    }
  }, [bookingParam, isResumeMode]);

  // One-shot viewport snapshot on first mount. Diagnoses Android/Samsung Internet
  // dropoff where users land mid-page. Runs before scroll-to-top below so it
  // captures the actual landing position.
  const viewportSnapshotFiredRef = useRef(false);
  useEffect(() => {
    if (viewportSnapshotFiredRef.current) return;
    if (typeof window === "undefined") return;
    viewportSnapshotFiredRef.current = true;
    trackQuoteViewportSnapshot({
      scrollY: window.scrollY,
      scrollHeight: document.documentElement.scrollHeight,
      innerHeight: window.innerHeight,
      visualViewportHeight: window.visualViewport?.height ?? 0,
      devicePixelRatio: window.devicePixelRatio,
      userAgent: navigator.userAgent,
      isIframe,
      isResumeMode,
      initialPath: currentPath,
    });
  }, []);

  // Disable browser scroll restoration so it doesn't fight our scroll-to-top.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!("scrollRestoration" in window.history)) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  // Reset scroll on every step navigation (and initial mount). Fixes Android /
  // Samsung Internet landing mid-page on URL-driven step changes.
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  // Fire a quote_step_view event whenever the user lands on a known step.
  // Pairs with trackQuoteStepComplete (fired on the explicit "next" click) so
  // the funnel chart has both arrival and departure counts.
  useEffect(() => {
    if (current < 0) return;
    const step = stepOrder[current];
    if (!step) return;
    const stepName = stepToAnalyticsName[step];
    if (!stepName) return;
    trackQuoteStepView(
      "control",
      stepName,
      quoteData.selectedProcedures.length,
      quoteData.selectedProcedures.map((p) => p.id),
    );
    // Intentionally only depend on pathname — don't refire when the procedure
    // list mutates while the user is on a single step.
  }, [location.pathname]);

  // if there is only one location, its preselected
  const onlyOneLocation = quoteData.locationOptions.length === 1;

  // RETIRED 2026-09-13: a Shift+ArrowRight document-level hotkey used to jump
  // `current + 2` — skipping a whole step with no validation and no save. It
  // was a QA shortcut, but it shipped to every patient, and anything that
  // bypasses `next()` can strand a lead mid-funnel with unsaved answers.
  // Normal single-step navigation covers the QA need; nothing replaces it.

  const selectLocation = (location: string) => {
    quoteData.setLocation(location);
    const nextIndex = Math.min(current + 1, stepOrder.length - 1);
    const nextStepPath = stepToPath[stepOrder[nextIndex]];
    navigateToStep(nextStepPath);
  };

  const next = useCallback(
    (skipSave = false) => {
      // Block navigation in legacy single-step resume modes (photos / book-now).
      // Continue-link mode (e.g. /id/<id>/medical-history) advances normally.
      if (isResumeMode && !isContinueMode) {
        message.info(t("validation.reviewMode"));
        return;
      }

      // Guard against simultaneous navigation (prevents race conditions in DevTools mobile simulation)
      if (navigationInProgressRef.current) {
        return;
      }
      navigationInProgressRef.current = true;

      if (
        current ===
          stepOrder.findIndex((step) => step === Step.SelectLocation) &&
        quoteData.location === ""
      ) {
        navigationInProgressRef.current = false;
        message.error(t("validation.selectLocation"));
        return undefined;
      }

      if (
        current ===
          stepOrder.findIndex((step) => step === Step.SelectProcedure) &&
        quoteData.selectedProcedures.length === 0 &&
        quoteData.selectedProcedureTypes.length === 0
      ) {
        navigationInProgressRef.current = false;
        message.error(t("validation.selectProcedure"));
        return undefined;
      }

      //if there are no surgeons in the location skip the surgeon selection page
      if (
        current ===
          stepOrder.findIndex((step) => step === Step.SelectSurgeon) &&
        quoteData.surgeons.length > 0
      ) {
        const surgeonsInLocation = quoteData.surgeons.some(
          (surgeon: Surgeon) => {
            const surgeonInThisLocation = surgeon.businessLocations.some(
              (location) => {
                return location.id === quoteData.location;
              },
            );

            return surgeonInThisLocation;
          },
        );

        if (!surgeonsInLocation) {
          navigationInProgressRef.current = false;
          const nextIndex = Math.min(current + 2, stepOrder.length - 1);
          const nextStepPath = stepToPath[stepOrder[nextIndex]];
          navigateToStep(nextStepPath);
          return;
        }
      }

      if (
        current ===
          stepOrder.findIndex((step) => step === Step.SelectSurgeon) &&
        !quoteData.selectedSurgeon
      ) {
        navigationInProgressRef.current = false;
        message.error(t("validation.selectSurgeon"));
        return undefined;
      }

      // Backstop for the Contact step's CTA gate. It derives the missing set
      // from the SAME helper the button's disabled state uses
      // (useQuoteFormValidation), so the two can no longer disagree — the
      // 2026-09-13 bug was exactly that: lastName blocked here but not there,
      // so the button rendered enabled and the tap bounced on a generic toast.
      // The toast now names what's actually missing, in the patient's language.
      if (current === stepOrder.findIndex((step) => step === Step.Contact)) {
        const missingContact = missingContactFields({
          name: quoteData.name,
          lastName: quoteData.lastName,
          email: quoteData.email,
          phone: quoteData.phone,
          zipCode: quoteData.zipCode,
          hipaaAgreement: quoteData.hipaaAgreement,
        });
        if (missingContact.length > 0) {
          navigationInProgressRef.current = false;
          message.error(
            t("validation.completeFields", {
              fields: missingContact
                .map((field) => t(CONTACT_FIELD_LABEL_KEYS[field]))
                .join(", "),
            }),
          );
          return undefined;
        }
      }

      if (
        current ===
          stepOrder.findIndex((step) => step === Step.ReadinessTimeline) &&
        !quoteData.patientReadiness
      ) {
        navigationInProgressRef.current = false;
        message.error(t("validation.readinessRequired"));
        return undefined;
      }

      if (isMobile) {
        window.scrollTo(0, 0);
      }

      if (
        current === stepOrder.findIndex((step) => step === Step.SelectProcedure)
      ) {
        logEvent("User", "Clicked Button", "StepSelectProcedure:Get my quote");
        // Track step completion for analytics
        trackQuoteStepComplete(
          "control",
          "procedures",
          quoteData.selectedProcedures.length,
          quoteData.selectedProcedures.map((p) => p.id),
        );

        // A draft is the pre-contact placeholder; never mint one when a real
        // quote is already loaded (the ?quote_id= lane after an in-funnel Back
        // has no quote_id in the URL, so the provider's incomingQuoteId guard
        // alone would not stop it).
        if (
          !quoteData.draftQuoteId &&
          !isResumeMode &&
          quoteData.quoteId === ""
        ) {
          quoteData
            .startDraftQuote({ lastStep: "procedures" })
            .then((draftId) => {
              if (!draftId) return;
              trackQuoteDraftStarted(
                "control",
                quoteData.selectedProcedures.length,
                quoteData.selectedProcedures.map((p) => p.id),
              );
            })
            .catch(() => null);
        }
      }

      if (
        current === stepOrder.findIndex((step) => step === Step.SelectSurgeon)
      ) {
        logEvent("User", "Clicked Button", "StepSelectSurgeon:Next");
      }

      if (
        current === stepOrder.findIndex((step) => step === Step.MedicalHistory)
      ) {
        logEvent("User", "Clicked Button", "StepMedicalHistory:Next");
      }

      if (
        current ===
        stepOrder.findIndex((step) => step === Step.ReadinessTimeline)
      ) {
        logEvent("User", "Clicked Button", "StepReadinessTimeline:Next");
      }

      if (!skipSave) {
        const isContactStep =
          current === stepOrder.findIndex((step) => step === Step.Contact);
        if (isContactStep) {
          // Welcome email is now sent on Photos arrival (see StepPhotos.tsx)
          // so it lands while the user is reading the photo-discount modal.
          quoteData.submitPersonalizedQuote();
          trackQuoteStepComplete(
            contactSweepsVariant,
            "contact",
            quoteData.selectedProcedures.length,
            quoteData.selectedProcedures.map((p) => p.id),
          );
          trackQuoteContactSubmit(
            contactSweepsVariant,
            quoteData.selectedProcedures.length,
            quoteData.selectedProcedures.map((p) => p.id),
          );
        } else {
          quoteData.submitPersonalizedQuote();
        }
      }

      if (
        current === stepOrder.findIndex((step) => step === Step.SelectProcedure)
      ) {
        const nextAvailableStep = stepOrder.findIndex(
          (step) => step === Step.Contact,
        );
        if (quoteData.quoteId !== "" && nextAvailableStep !== -1) {
          navigationInProgressRef.current = false;
          const nextIndex = Math.min(current + 1, stepOrder.length - 1);
          const nextStepPath = stepToPath[stepOrder[nextIndex]];
          navigateToStep(nextStepPath);
          return;
        }
      }

      // Navigate to next step
      const nextIndex = Math.min(current + 1, stepOrder.length - 1);
      const nextStepPath = stepToPath[stepOrder[nextIndex]];
      navigateToStep(nextStepPath);
    },
    [
      current,
      stepOrder,
      quoteData,
      navigate,
      screen,
      logEvent,
      isResumeMode,
      isContinueMode,
      t,
    ],
  );

  const prev = () => {
    // Block back-nav in legacy single-step resume modes (photos / book-now).
    // Continue-link mode allows back-navigation through the funnel.
    if (isResumeMode && !isContinueMode) {
      return;
    }

    if (
      current === stepOrder.findIndex((step) => step === Step.SelectSurgeon)
    ) {
      const prevIndex = Math.max(current - 2, 0);
      const prevStepPath = stepToPath[stepOrder[prevIndex]];
      navigateToStep(prevStepPath);
      return;
    }
    const prevIndex = Math.max(current - 1, 0);
    const prevStepPath = stepToPath[stepOrder[prevIndex]];
    navigateToStep(prevStepPath);
  };

  let stepNumber = 0;

  // `stepOrder` above is AUTHORITATIVE for which steps exist in this session —
  // every conditional group below asks it rather than re-deriving the same
  // condition, so the two lists cannot drift apart (before 2026-09-13 the
  // Medical/Readiness pair was gated on `!quoteReloaded || isContinueMode`
  // here but `!quoteReloaded && !isSignedIn` there, and only the
  // `filteredSteps` pass below hid the mismatch — at the cost of a skipped
  // step number for signed-in visitors). Keep the ORDER of this array in sync
  // with stepOrder's; membership now takes care of itself.
  const steps = [
    ...(stepOrder.includes(Step.SelectLocation)
      ? [
          {
            stepName: Step.SelectLocation,
            title: !isMobile && (
              <>
                <div className="stepNumber">0{stepNumber}</div>
                <div>Select Location</div>
              </>
            ),
            content: <StepSelectLocation selectLocation={selectLocation} />,
            icon: <EnvironmentOutlined />,
          },
        ]
      : []),
    {
      stepName: Step.SelectProcedure,
      title: !isMobile && (
        <div>
          <div className="stepNumber">0{stepNumber++}</div>
          <div>Select procedure</div>
        </div>
      ),
      content: (
        <StepSelectProcedure
          next={() => {
            next();
          }}
        />
      ),
      icon: <FileAddOutlined />,
    },
    ...(stepOrder.includes(Step.Contact)
      ? [
          {
            stepName: Step.Contact,
            title: !isMobile && (
              <div>
                <div className="stepNumber">0{stepNumber++}</div>
                <div>Contact</div>
              </div>
            ),
            content: <StepContact next={next} />,
            icon: <HeartOutlined />,
          },
        ]
      : []),
    ...(stepOrder.includes(Step.MedicalHistory)
      ? [
          {
            stepName: Step.MedicalHistory,
            title: !isMobile && (
              <div>
                <div className="stepNumber">0{stepNumber++}</div>
                <div>Medical Info</div>
              </div>
            ),
            content: <StepMedicalHistory next={next} />,
            icon: <FileTextOutlined />,
          },
          {
            stepName: Step.ReadinessTimeline,
            title: !isMobile && (
              <div>
                <div className="stepNumber">0{stepNumber++}</div>
                <div>Readiness</div>
              </div>
            ),
            content: <StepReadinessTimeline next={next} />,
            icon: <FileTextOutlined />,
          },
        ]
      : []),
    ...(stepOrder.includes(Step.SelectSurgeon)
      ? [
          {
            stepName: Step.SelectSurgeon,
            title: !isMobile && (
              <div>
                <div className="stepNumber">0{stepNumber++}</div>
                <div>Get quote</div>
              </div>
            ),
            content: (
              <Suspense>
                <StepSelectSurgeon
                  next={() => {
                    // next(true);
                    // this happens in the last step
                    // if its not doctor miami, redirect to scheduling (do not show modal)
                    quoteData.submitPersonalizedQuote();

                    next();
                    // if (
                    //   quoteData.selectedSurgeon?.id !== "64f7fac0f5661576d884ff89"
                    // ) {
                    //   message.success(
                    //     "Submitting photos, redirecting to scheduling..."
                    //   );
                    // } else {
                    //   setIsModalOpen(true);
                    //   message.success("Processing complete");
                    // }
                  }}
                />
              </Suspense>
            ),
            icon: <FileTextOutlined />,
          },
        ]
      : []),
    {
      stepName: Step.Photos,
      title: !isMobile && (
        <div>
          <div className="stepNumber">0{stepNumber++}</div>
          <div>Photos</div>
        </div>
      ),
      content: (
        <StepPhotos
          isResumeMode={isResumeMode}
          next={() => {
            if (!quoteData.hipaaAgreement && !isSignedIn) {
              message.error(t("contact.hipaaError"));
              return;
            }

            logEvent("User", "Clicked Button", "StepPhotos:Submit");

            quoteData.submitPersonalizedQuote({ redirectOnComplete: false });

            setIsModalOpen(true);
            message.success(t("validation.processingComplete"));

            // next();
          }}
        />
      ),
      icon: <FileImageOutlined />,
    },
  ];

  // Filter steps to only include those in the current stepOrder
  const filteredSteps = steps.filter((step) =>
    stepOrder.includes(step.stepName),
  );

  const items = filteredSteps.map((item) => ({
    key: item.title,
    title: item.title,
    icon: item.icon,
  }));

  const currentStepName = filteredSteps[current]?.stepName;

  // Rendering current step

  // Dismiss the HTML boot curtain (#initial-loading) as soon as the first
  // step has actually painted — not on a blind timer. We wait one frame past
  // the point where the real content is ready (quoteData.isLoading clears, or
  // we're on a booking success/cancelled page) so the curtain lifts straight
  // onto rendered content: no artificial delay, and no spinner flashing over
  // already-loaded content. The opaque curtain still covers React's boot, and
  // the AntD data spin stays hidden behind it until content is ready.
  useEffect(() => {
    const showingDataSpin =
      quoteData.isLoading &&
      bookingParam !== "success" &&
      bookingParam !== "cancelled";
    if (showingDataSpin) return;

    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        (window as any).hideInitialLoading?.();
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [quoteData.isLoading, bookingParam]);

  // Note: Page view tracking is now handled automatically by React Router
  // as the URL changes with each step navigation

  const contentStyle: React.CSSProperties = {
    textAlign: "center",
    color: token.colorTextTertiary,
    backgroundColor: "#fafafa",
    minHeight: "calc(100dvh - 200px)",
    padding: isMobile ? "0" : "10px 15px",
  };

  const contentWrapperStyle: React.CSSProperties = {
    maxWidth: "1280px",
    margin: "0 auto",
    backgroundColor: "#ffffff",
    borderRadius: isMobile ? "0" : "14px",
    padding: isMobile ? "16px 16px 24px 16px" : "30px 20px",
    border: isMobile ? "none" : "1px solid #e5e5e5",
  };

  const shownItems = useMemo(() => {
    switch (current) {
      case 0:
      case 1:
      case 2:
      case 3:
        return items.slice(0, 4);
      case 4:
        return items.slice(0, 5);
      case 5:
        return items.slice(0, 6);
      default:
        return items;
    }
  }, [items]);

  useEffect(() => {
    logEvent("User", "Loaded app", "QuoteApp loaded");
  }, []);

  // Landing rule for a quote that arrives through the URL without the
  // /id/<id> resume prefix — a `?quote_id=` reload (the thank-you page's
  // "Continue to your Free Evaluation" handoff, #2242) or a shared
  // `?selected_procedures[]=` link: start the patient at the first step.
  //
  // ONE-SHOT. `currentPath` is a dependency (the redirect needs the current
  // step to know whether it is already home), so without the latch this
  // re-fired on EVERY step change and replace-navigated the patient straight
  // back to /select-procedure the moment Continue pushed /photos — the
  // 2026-10-15 owner report: hero/contact-form leads could select a procedure
  // but never leave that step. The effect has been path-keyed since
  // 2026-05-04 (44b6f18f5); the thank-you handoff (2026-09-13) was its first
  // live producer of `?quote_id=`, so that lane was trapped from day one. The
  // emailed resume links never hit it because they carry the /id/<id> prefix
  // (`isResumeMode`) and skip this rule.
  const landingRedirectDoneRef = useRef(false);
  useEffect(() => {
    if (landingRedirectDoneRef.current) return;
    if (stepOrder.length === 0) return;

    const reloadedLanding =
      quoteData.quoteReloaded && !isResumeMode && onlyOneLocation;
    const sharedLanding = quoteData.quoteShared && onlyOneLocation;
    if (!reloadedLanding && !sharedLanding) return;

    // Latch BEFORE deciding whether to move: a landing that is already on the
    // first step must also arm it, or the next step change would be bounced.
    landingRedirectDoneRef.current = true;

    const firstStepPath = stepToPath[stepOrder[0]];
    // Already at the target — avoid wasting a history.replaceState call.
    if (currentPath === firstStepPath) return;

    // Carry the query string: dropping `?quote_id=` here unbinds the loaded
    // quote from the URL, and the procedures step's Continue would then mint a
    // draft quote for a lead that already exists.
    navigate(
      { pathname: firstStepPath, search: location.search },
      { replace: true },
    );
  }, [
    quoteData.quoteReloaded,
    quoteData.quoteShared,
    onlyOneLocation,
    stepOrder,
    navigate,
    isResumeMode,
    currentPath,
    location.search,
  ]);

  // Render booking success/cancelled pages inline when returning from Stripe
  if (bookingParam === "success") {
    return <BookingSuccessPage quoteId={quoteId} />;
  }
  if (bookingParam === "cancelled") {
    return <BookingCancelledPage quoteId={quoteId} />;
  }

  if (quoteData.isLoading) {
    return <Spin spinning={true} fullscreen />;
  }

  if (renderError) {
    console.error("[QuoteApp] Render error:", renderError);
    return (
      <div style={{ padding: "40px", color: "red" }}>
        <h2>Error rendering quote app</h2>
        <p>{renderError.message}</p>
        <p>Check console for details</p>
      </div>
    );
  }

  try {
    return (
      <>
        {/* <QuotePromoModal /> */}
        <GlobalStyle background={appConfig?.appBackground} />
        <div className="quote-app-container" style={{}}>
          <InAppBrowserBanner />
          <FriendsPassBanner />
          <div>
            {current > 0 && current < filteredSteps.length && (
              <button
                type="button"
                onClick={() => prev()}
                aria-label="Back"
                className="quote-back-button"
                style={{
                  position: "fixed",
                  top: isMobile
                    ? "calc(52px + env(safe-area-inset-top, 0px))"
                    : "160px",
                  right: "16px",
                  zIndex: 10,
                }}
              >
                <LeftCircleOutlined />
              </button>
            )}
          </div>
          {!(isMobile && currentStepName === Step.Photos) && (
            <div className={`quote-header ${isMobile ? "quote-header--mobile" : ""}`}>
              <Row align="middle" justify="start" style={{ flex: 1 }}>
                <Col span={isMobile ? 24 : 10} style={{ paddingBottom: "0" }}>
                  <a
                    href={topAestheticsUrl}
                    className="quote-header__brand"
                  >
                    <img
                      src={logo}
                      alt="Logo"
                      className="quote-header__logo"
                    />
                    <h1 className="quote-header__title">
                      <span className="quote-header__title-strong">
                        Top Aesthetics
                      </span>
                      <span className="quote-header__subtitle">
                        {t("header.freeInstantQuote")}
                      </span>
                    </h1>
                  </a>
                </Col>
                {/* {!isMobile && (
                <Col xs={24} md={13} offset={!isMobile ? 1 : 0}>
                  <Steps
                    style={{ padding: 15 }}
                    responsive={false}
                    current={current}
                    items={shownItems}
                  />
                </Col>
              )} */}
                {!isMobile && (
                  <Col span={13} offset={1}>
                    <div style={{ paddingTop: "10px" }}>
                      <Progress
                        percent={Math.round(
                          (current / (filteredSteps.length - 1)) * 100,
                        )}
                        size={["80%", 28]}
                      />
                    </div>
                  </Col>
                )}
              </Row>
              <div className="quote-header__right">
                <LanguageSwitcher />
              </div>
            </div>
          )}

          {isMobile && (
            <div className="quote-progress-mobile">
              <div
                className="quote-progress-mobile-fill"
                style={{
                  width: `${Math.max(
                    4,
                    Math.round(
                      (current / Math.max(1, filteredSteps.length - 1)) * 100,
                    ),
                  )}%`,
                }}
              />
            </div>
          )}

          <div style={contentStyle}>
            <div style={contentWrapperStyle}>
              {(() => {
                try {
                  if (!filteredSteps[current]) {
                    console.error(
                      `[QuoteApp] Error: Step ${current} not found in filteredSteps array`,
                    );
                    return (
                      <div>
                        Error: Step not found (current={current}, total steps=
                        {filteredSteps.length})
                      </div>
                    );
                  }

                  return filteredSteps[current].content;
                } catch (err) {
                  console.error("[QuoteApp] Error rendering step:", err);
                  return <div>Error rendering step: {String(err)}</div>;
                }
              })()}
            </div>
          </div>
          <Modal
            open={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            footer={null}
          >
            <QuoteSuccessModal
              bookedContext={bookingParam === "success"}
              onClose={() => setIsModalOpen(false)}
            />
          </Modal>
        </div>
      </>
    );
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    console.error("[QuoteApp] Caught error during render:", error);
    setRenderError(error);
    return (
      <div style={{ padding: "40px", color: "red" }}>
        <h2>Error rendering quote app</h2>
        <p>{error.message}</p>
        <p>Check console for details</p>
      </div>
    );
  }
};

export default QuoteApp;

const GlobalStyle = createGlobalStyle<GlobalStyleProps>`
  html {
    height: 100%;
    background: #f9fafb;
    background-attachment:fixed;
  }
  body {
    min-height: 100vh;
    margin: 0;
    background-color: #f9fafb !important;
    color: #0c0a09 !important;
  }
`;
